import { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { FolderOpen, ChevronLeft, ChevronRight, Calendar, User } from '../../icons/wp-icons';
import { getAllProjekte } from '../../api/projektApi';

const STATUS_COLORS = {
  PLANUNG: { bg: 'bg-gray-400', border: 'border-gray-500', text: 'Planung' },
  IN_BEARBEITUNG: { bg: 'bg-blue-500', border: 'border-blue-600', text: 'In Bearbeitung' },
  ABGESCHLOSSEN: { bg: 'bg-green-500', border: 'border-green-600', text: 'Abgeschlossen' },
  ABGERECHNET: { bg: 'bg-purple-500', border: 'border-purple-600', text: 'Abgerechnet' }
};

export default function ProjektZeitstrahl() {
  const navigate = useNavigate();
  const containerRef = useRef(null);
  const [projekte, setProjekte] = useState([]);
  const [loading, setLoading] = useState(true);
  const [viewStart, setViewStart] = useState(null);
  const [viewMonths, setViewMonths] = useState(3);
  const [hoveredProjekt, setHoveredProjekt] = useState(null);

  useEffect(() => {
    loadProjekte();
    // Setze initialen Zeitraum auf aktuellen Monat
    const now = new Date();
    setViewStart(new Date(now.getFullYear(), now.getMonth(), 1));
  }, []);

  const loadProjekte = async () => {
    try {
      const data = await getAllProjekte();
      // Nur Projekte mit Start- oder Enddatum
      const mitDatum = data.filter(p => p.startDatum || p.endDatum);
      setProjekte(mitDatum);
    } catch (error) {
      console.error('Fehler beim Laden der Projekte:', error);
    } finally {
      setLoading(false);
    }
  };

  // Navigation
  const goToPrev = () => {
    setViewStart(prev => {
      const newDate = new Date(prev);
      newDate.setMonth(newDate.getMonth() - 1);
      return newDate;
    });
  };

  const goToNext = () => {
    setViewStart(prev => {
      const newDate = new Date(prev);
      newDate.setMonth(newDate.getMonth() + 1);
      return newDate;
    });
  };

  const goToToday = () => {
    const now = new Date();
    setViewStart(new Date(now.getFullYear(), now.getMonth(), 1));
  };

  if (!viewStart) return null;

  // Berechne Zeitraum-Grenzen
  const viewEnd = new Date(viewStart);
  viewEnd.setMonth(viewEnd.getMonth() + viewMonths);

  // Generiere Monate für Header
  const months = [];
  const currentMonth = new Date(viewStart);
  while (currentMonth < viewEnd) {
    months.push(new Date(currentMonth));
    currentMonth.setMonth(currentMonth.getMonth() + 1);
  }

  // Berechne Tage im Zeitraum
  const totalDays = Math.ceil((viewEnd - viewStart) / (1000 * 60 * 60 * 24));

  // Heute-Position
  const today = new Date();
  const todayOffset = Math.max(0, Math.min(100, ((today - viewStart) / (viewEnd - viewStart)) * 100));
  const isTodayVisible = today >= viewStart && today <= viewEnd;

  // Berechne Position und Breite für ein Projekt
  const getProjectBar = (projekt) => {
    const start = projekt.startDatum ? new Date(projekt.startDatum) : null;
    const end = projekt.endDatum ? new Date(projekt.endDatum) : null;

    // Wenn kein Datum, nicht anzeigen
    if (!start && !end) return null;

    // Verwende Defaults wenn nur ein Datum vorhanden
    const effectiveStart = start || end;
    const effectiveEnd = end || start;

    // Clamp zum sichtbaren Bereich
    const clampedStart = new Date(Math.max(effectiveStart.getTime(), viewStart.getTime()));
    const clampedEnd = new Date(Math.min(effectiveEnd.getTime(), viewEnd.getTime()));

    // Prüfe ob im sichtbaren Bereich
    if (clampedStart >= viewEnd || clampedEnd <= viewStart) return null;

    const left = ((clampedStart - viewStart) / (viewEnd - viewStart)) * 100;
    const width = ((clampedEnd - clampedStart) / (viewEnd - viewStart)) * 100;

    // Mindestbreite für Klickbarkeit
    const minWidth = 2;

    return {
      left: `${left}%`,
      width: `${Math.max(width, minWidth)}%`,
      startsBeforeView: effectiveStart < viewStart,
      endsAfterView: effectiveEnd > viewEnd
    };
  };

  const formatMonthYear = (date) => {
    return date.toLocaleDateString('de-DE', { month: 'short', year: 'numeric' });
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    return new Date(dateStr).toLocaleDateString('de-DE', { day: '2-digit', month: '2-digit', year: 'numeric' });
  };

  // Gruppiere Projekte nach Überlappung für Row-Zuweisung
  const sortedProjekte = [...projekte].sort((a, b) => {
    const aStart = a.startDatum ? new Date(a.startDatum) : new Date(a.endDatum);
    const bStart = b.startDatum ? new Date(b.startDatum) : new Date(b.endDatum);
    return aStart - bStart;
  });

  if (loading) {
    return (
      <div className="bg-surface rounded-card shadow-card p-6">
        <div className="flex justify-center py-8">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-surface rounded-card shadow-card overflow-hidden">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between p-3 sm:p-4 border-b border-border gap-2 sm:gap-0">
        <div className="flex items-center gap-2 sm:gap-3">
          <FolderOpen className="w-5 h-5 text-primary" />
          <h3 className="font-semibold text-text text-sm sm:text-base">Projekt-Zeitstrahl</h3>
          <span className="text-xs sm:text-sm text-text-muted">({projekte.length})</span>
        </div>

        <div className="flex items-center justify-between sm:justify-end gap-2">
          {/* Zeitraum-Auswahl */}
          <select
            value={viewMonths}
            onChange={(e) => setViewMonths(parseInt(e.target.value))}
            className="px-2 py-1.5 text-xs sm:text-sm border border-border rounded bg-surface text-text"
          >
            <option value={2}>2 Mon.</option>
            <option value={3}>3 Mon.</option>
            <option value={6}>6 Mon.</option>
            <option value={12}>12 Mon.</option>
          </select>

          {/* Navigation */}
          <div className="flex items-center gap-0.5 sm:gap-1">
            <button
              onClick={goToPrev}
              className="p-2 sm:p-1.5 hover:bg-surface-elevated rounded transition"
              title="Vorheriger Monat"
            >
              <ChevronLeft className="w-5 h-5 sm:w-4 sm:h-4" />
            </button>
            <button
              onClick={goToToday}
              className="px-2 py-1.5 text-xs sm:text-sm hover:bg-surface-elevated rounded transition"
            >
              Heute
            </button>
            <button
              onClick={goToNext}
              className="p-2 sm:p-1.5 hover:bg-surface-elevated rounded transition"
              title="Nächster Monat"
            >
              <ChevronRight className="w-5 h-5 sm:w-4 sm:h-4" />
            </button>
          </div>
        </div>
      </div>

      {/* Legende - horizontal scrollbar auf Mobile */}
      <div className="flex gap-3 sm:gap-4 px-3 sm:px-4 py-2 bg-surface-elevated border-b border-border text-xs overflow-x-auto">
        {Object.entries(STATUS_COLORS).map(([key, { bg, text }]) => (
          <div key={key} className="flex items-center gap-1.5 whitespace-nowrap">
            <div className={`w-3 h-3 rounded ${bg}`} />
            <span className="text-text-muted">{text}</span>
          </div>
        ))}
      </div>

      {/* Timeline Container */}
      <div ref={containerRef} className="relative">
        {/* Monate Header */}
        <div className="flex border-b border-border bg-gray-50 sticky top-0 z-10">
          {months.map((month, idx) => {
            const daysInMonth = new Date(month.getFullYear(), month.getMonth() + 1, 0).getDate();
            const monthStart = new Date(Math.max(month.getTime(), viewStart.getTime()));
            const monthEnd = new Date(month.getFullYear(), month.getMonth() + 1, 0);
            const effectiveEnd = new Date(Math.min(monthEnd.getTime(), viewEnd.getTime()));
            const daysVisible = Math.ceil((effectiveEnd - monthStart) / (1000 * 60 * 60 * 24)) + 1;
            const widthPercent = (daysVisible / totalDays) * 100;

            return (
              <div
                key={idx}
                className="text-center py-2 text-sm font-medium text-text border-r border-border last:border-r-0"
                style={{ width: `${widthPercent}%` }}
              >
                {formatMonthYear(month)}
              </div>
            );
          })}
        </div>

        {/* Wochen-Indikatoren */}
        <div className="absolute top-8 left-0 right-0 h-full pointer-events-none">
          {/* Vertikale Linien für Wochen */}
          {Array.from({ length: Math.ceil(totalDays / 7) }).map((_, idx) => {
            const weekStart = new Date(viewStart);
            weekStart.setDate(weekStart.getDate() + idx * 7);
            const offset = ((weekStart - viewStart) / (viewEnd - viewStart)) * 100;
            if (offset < 0 || offset > 100) return null;
            return (
              <div
                key={idx}
                className="absolute top-0 bottom-0 w-px bg-gray-200"
                style={{ left: `${offset}%` }}
              />
            );
          })}
        </div>

        {/* Heute-Linie */}
        {isTodayVisible && (
          <div
            className="absolute top-8 bottom-0 w-0.5 bg-red-500 z-20"
            style={{ left: `${todayOffset}%` }}
          >
            <div className="absolute -top-1 -left-2 w-4 h-4 bg-red-500 rounded-full flex items-center justify-center">
              <div className="w-2 h-2 bg-white rounded-full" />
            </div>
          </div>
        )}

        {/* Projekte */}
        <div className="relative min-h-[200px] py-2">
          {projekte.length === 0 ? (
            <div className="flex items-center justify-center h-32 text-text-muted">
              <div className="text-center">
                <Calendar className="w-8 h-8 mx-auto mb-2 opacity-50" />
                <p className="text-sm">Keine Projekte mit Datum vorhanden</p>
              </div>
            </div>
          ) : (
            sortedProjekte.map((projekt, idx) => {
              const bar = getProjectBar(projekt);
              if (!bar) return null;

              const statusConfig = STATUS_COLORS[projekt.status] || STATUS_COLORS.PLANUNG;
              const isHovered = hoveredProjekt === projekt.id;

              return (
                <div
                  key={projekt.id}
                  className="relative px-2 sm:px-4 mb-1"
                  style={{ marginTop: idx === 0 ? '0' : '4px' }}
                >
                  {/* Mobile: Vertikal Layout */}
                  <div className="sm:hidden">
                    <button
                      onClick={() => navigate(`/projekte/${projekt.id}`)}
                      className="w-full text-left"
                    >
                      <div className="text-xs font-medium text-text truncate mb-1 px-1">
                        {projekt.titel}
                      </div>
                      <div className="relative h-7">
                        <div
                          className={`absolute top-0 h-full rounded-md cursor-pointer ${statusConfig.bg}`}
                          style={{
                            left: bar.left,
                            width: bar.width,
                            minWidth: '40px',
                            borderRadius: bar.startsBeforeView ? '0 6px 6px 0' : bar.endsAfterView ? '6px 0 0 6px' : '6px'
                          }}
                        >
                          <div className="absolute inset-0 flex items-center justify-center px-1 overflow-hidden">
                            <span className="text-white text-xs font-medium truncate">
                              {projekt.nummer}
                            </span>
                          </div>
                        </div>
                      </div>
                    </button>
                  </div>

                  {/* Desktop: Horizontal Layout */}
                  <div className="hidden sm:block relative h-10">
                    {/* Projekt-Name links */}
                    <div className="absolute left-0 top-0 h-full flex items-center pl-2 z-10 w-[200px]">
                      <button
                        onClick={() => navigate(`/projekte/${projekt.id}`)}
                        className="text-sm font-medium text-text hover:text-primary truncate max-w-[190px] text-left"
                        title={projekt.titel}
                      >
                        {projekt.titel}
                      </button>
                    </div>

                    {/* Timeline-Bereich */}
                    <div className="absolute top-0 h-full left-[200px] right-0">
                      {/* Balken */}
                      <div
                        className={`absolute top-1/2 -translate-y-1/2 h-6 rounded-full cursor-pointer transition-all ${statusConfig.bg} ${isHovered ? 'h-7 shadow-lg' : ''}`}
                        style={{
                          left: bar.left,
                          width: bar.width,
                          borderRadius: bar.startsBeforeView ? '0 9999px 9999px 0' : bar.endsAfterView ? '9999px 0 0 9999px' : '9999px'
                        }}
                        onClick={() => navigate(`/projekte/${projekt.id}`)}
                        onMouseEnter={() => setHoveredProjekt(projekt.id)}
                        onMouseLeave={() => setHoveredProjekt(null)}
                      >
                        {/* Projekt-Info im Balken */}
                        <div className="absolute inset-0 flex items-center justify-center px-2 overflow-hidden">
                          <span className="text-white text-xs font-medium truncate">
                            {projekt.nummer}
                          </span>
                        </div>
                      </div>

                      {/* Tooltip bei Hover */}
                      {isHovered && (
                        <div
                          className="absolute z-30 bg-white shadow-xl rounded-lg border border-border p-3 min-w-[220px]"
                          style={{
                            left: bar.left,
                            top: '100%',
                            marginTop: '4px',
                            transform: 'translateX(-20%)'
                          }}
                        >
                          <p className="font-semibold text-text text-sm mb-1">{projekt.titel}</p>
                          <p className="text-xs text-text-muted mb-2">{projekt.nummer}</p>
                          <div className="flex items-center gap-4 text-xs text-text-muted">
                            <span className="flex items-center gap-1">
                              <Calendar className="w-3 h-3" />
                              {formatDate(projekt.startDatum)} - {formatDate(projekt.endDatum)}
                            </span>
                          </div>
                          {projekt.kunde && (
                            <div className="flex items-center gap-1 mt-1 text-xs text-text-muted">
                              <User className="w-3 h-3" />
                              {projekt.kunde.name}
                            </div>
                          )}
                          <div className="mt-2 pt-2 border-t border-border">
                            <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs text-white ${statusConfig.bg}`}>
                              {statusConfig.text}
                            </span>
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              );
            })
          )}
        </div>
      </div>
    </div>
  );
}
